import Button from "../../../shared/components/Button";
import Spinner from "../../../shared/components/Spinner";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { DraftPair } from "../../challenges/types/challengeDraft";
import {
  formatPaymentStatusLabel,
  formatRegistrationShirtSizes,
  formatRegistrationStatusLabel,
} from "../../events/shared/utils/eventRegistrationFormatters";
import type { AdminEventRegistration } from "../services/eventRegistrationAdminService";
import AdminRegistrationRowActions from "./AdminRegistrationRowActions";

type PaymentMethod = "pix" | "credit_card" | "debit_card";

type AdminSelectedPairsListProps = {
  adminPaymentMethod: PaymentMethod;
  canAddRegistrations: boolean;
  isAdmin: boolean;
  isTournamentChallenge: boolean;
  loading: boolean;
  onAddTournamentPair: () => void;
  onCopyPaymentLink: (
    id: number,
    paymentMethod?: PaymentMethod,
    existingPaymentLink?: string | null,
  ) => void;
  onDeleteRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => void;
  onEditAvailabilityRestriction?: (registration: AdminEventRegistration) => void;
  onEditTournamentPair: (registration: AdminEventRegistration) => void;
  onMarkPaidRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => void;
  onRemovePair: (pairId: string) => void;
  pairCapacity: {
    exceeds: boolean;
    limit: number;
  };
  pairCapacityUnit?: "inscritos" | "duplas";
  pairs: DraftPair[];
  registrationByPairId: Record<string, AdminEventRegistration>;
};

export default function AdminSelectedPairsList({
  adminPaymentMethod,
  canAddRegistrations,
  isAdmin,
  isTournamentChallenge,
  loading,
  onAddTournamentPair,
  onCopyPaymentLink,
  onDeleteRegistration,
  onEditAvailabilityRestriction,
  onEditTournamentPair,
  onMarkPaidRegistration,
  onRemovePair,
  pairCapacity,
  pairCapacityUnit = "inscritos",
  pairs,
  registrationByPairId,
}: AdminSelectedPairsListProps) {
  const t = siteTexts.admin;

  const restrictionSummary = (registration: AdminEventRegistration) => {
    const details = registration.availabilityRestriction?.details || [];
    const labels = details
      .map((item) => String(item.label || "").trim())
      .filter(Boolean);
    const fallback = String(registration.availabilityRestriction?.label || "").trim();
    const summary = labels.length > 0 ? labels.join(" • ") : fallback;
    return registration.availabilityRestriction?.hasRestriction && summary
      ? summary
      : "";
  };

  return (
    <section className="lista-duplas lista-duplas-fixed">
      <div className="lista-duplas-header">
        <h4>{siteTexts.challengeEngine.labels.registeredPairs}</h4>
        {isTournamentChallenge && (
          <Button
            type="button"
            variant="primary"
            className="admin-summary-add-button"
            disabled={!canAddRegistrations || pairCapacity.exceeds}
            title={
              pairCapacity.exceeds
                ? `Categoria atingiu o limite de ${pairCapacity.limit} ${pairCapacityUnit}.`
                : undefined
            }
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onAddTournamentPair();
            }}
          >
            Adicionar dupla
          </Button>
        )}
      </div>

      {loading && (
        <p className="status-message">
          <Spinner label={t.historyList.loadingChallengeRegistrations} />
        </p>
      )}

      {pairs.length === 0 ? (
        <p className="vazio">
          {siteTexts.challengeEngine.labels.nonePairRegistered}
        </p>
      ) : (
        pairs.map((pair) => {
          const registration = registrationByPairId[pair.id] || null;
          const shirtSizes = registration
            ? formatRegistrationShirtSizes(registration)
            : "";
          return (
            <div key={pair.id} className="dupla-item">
              <div className="challenge-registration-row-main">
                <span>{pair.label}</span>
                {registration && (
                  <>
                    <p className="challenge-registration-row-status">
                      {t.eventLink.registration}:{" "}
                      <strong>
                        {formatRegistrationStatusLabel(registration.status)}
                      </strong>
                      {" · "}
                      {t.registrationsTab.payment}:{" "}
                      <strong>
                        {registration.paymentRequired
                          ? formatPaymentStatusLabel(registration.paymentStatus)
                          : t.registrationsTab.notRequired}
                      </strong>
                    </p>
                    {shirtSizes && (
                      <p className="challenge-registration-row-status">
                        {t.registrationsTab.shirtSizes}:{" "}
                        <strong>{shirtSizes}</strong>
                      </p>
                    )}
                    {restrictionSummary(registration) && (
                      <p className="challenge-registration-row-status challenge-registration-row-status--restriction">
                        Restrição:{" "}
                        <strong>{restrictionSummary(registration)}</strong>
                      </p>
                    )}
                  </>
                )}
              </div>
              {registration ? (
                <AdminRegistrationRowActions
                  adminPaymentMethod={adminPaymentMethod}
                  isAdmin={isAdmin}
                  onCopyPaymentLink={onCopyPaymentLink}
                  onDeleteRegistration={onDeleteRegistration}
                  onEditAvailabilityRestriction={onEditAvailabilityRestriction}
                  onEditRegistration={onEditTournamentPair}
                  onMarkPaidRegistration={onMarkPaidRegistration}
                  registration={registration}
                  showEditAction={isTournamentChallenge}
                />
              ) : (
                <div className="challenge-registration-row-actions">
                  <Button type="button" variant="pill" disabled>
                    Processando inscrição...
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => onRemovePair(pair.id)}
                  >
                    {siteTexts.challengeEngine.labels.remove}
                  </Button>
                </div>
              )}
            </div>
          );
        })
      )}
    </section>
  );
}
